import { HttpStatus } from '@bioliberty/http';
import { MfaCodeInvalidError, MfaRequired } from '@bioliberty/lifemind';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { authorizeUrl, redirectTo, redirectToLogout } from '..';
import { useVerifyMfaMutation } from '../../api/auth';
import logo from '../../assets/bioliberty-lifemind-logo.svg';
import { Button } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { ErrorMessage, InputLabel } from '../../components/Input/TextInput';
import { MFA_SESSION_STORAGE_KEY, Route } from '../../constants';
import { setToken } from '../../stores/slices/auth';
import { cn } from '../../util';

type ChallengeState = 'entry' | 'incorrect' | 'expired' | 'lockedout' | 'generic-error';

// Digits only, capped at the code length — handles paste artifacts from the email (leading
// whitespace, trailing newline) and stray non-digit keystrokes, matching the backend's own
// `.trim()`-then-validate behaviour.
const sanitizeCode = (value: string): string => value.replace(/\D/g, '').slice(0, 6);

const isMfaRequired = (value: unknown): value is MfaRequired =>
  !!value && typeof value === 'object' && 'mfaSessionId' in value;

const restartSignIn = () => {
  sessionStorage.removeItem(MFA_SESSION_STORAGE_KEY);
  redirectTo(authorizeUrl());
};

// The lockedout user's Cognito session cookie is still live, so restartSignIn's
// redirectTo(Route.Authorize) would silently re-authenticate them straight back through
// /sign-in/callback into this same gate. Logging out clears that cookie, actually reaching login.
const backToLogin = () => {
  sessionStorage.removeItem(MFA_SESSION_STORAGE_KEY);
  redirectToLogout();
};

const readMfaSession = (routerState: unknown): MfaRequired | undefined => {
  if (isMfaRequired(routerState)) {
    return routerState;
  }

  const stored = sessionStorage.getItem(MFA_SESSION_STORAGE_KEY);
  if (!stored) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(stored);
    return isMfaRequired(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const MfaChallenge = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Read once: the session travels via router state on the happy path, or sessionStorage on refresh.
  const mfaSession = useMemo(() => readMfaSession(location.state), [location.state]);
  // Defensive === true check: a stale/older stored session without this field must not be
  // mistaken for a lockout.
  const [state, setState] = useState<ChallengeState>(() => (mfaSession?.lockedOut === true ? 'lockedout' : 'entry'));
  const [code, setCode] = useState('');
  const [verifyMfa, { isLoading }] = useVerifyMfaMutation();
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = useCallback(
    async (submittedCode: string) => {
      if (isLoading || !mfaSession) {
        return;
      }

      const result = await verifyMfa({ mfaSessionId: mfaSession.mfaSessionId, code: submittedCode });

      if ('data' in result && result.data?.accessToken) {
        dispatch(setToken(result.data));
        sessionStorage.removeItem(MFA_SESSION_STORAGE_KEY);
        navigate(sessionStorage.getItem('returnPath') ?? Route.Home);
        return;
      }

      setCode('');

      const error = 'error' in result ? result.error : undefined;
      if (error && 'status' in error) {
        if (error.status === HttpStatus.Forbidden) {
          const attemptsRemaining = (error.data as MfaCodeInvalidError | undefined)?.attemptsRemaining;
          if (typeof attemptsRemaining === 'number') {
            setState(attemptsRemaining > 0 ? 'incorrect' : 'lockedout');
            return;
          }
          // Malformed 403 body (e.g. from a proxy/WAF) — fall through to the generic error below.
        } else if (error.status === HttpStatus.NotFound) {
          setState('expired');
          return;
        }
      }

      setState('generic-error');
    },
    [isLoading, mfaSession, verifyMfa, dispatch, navigate]
  );

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeCode(event.target.value);
      setCode(sanitized);
      if (sanitized.length === 6) {
        submit(sanitized);
      }
    },
    [submit]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && code.length === 6) {
        submit(code);
      }
    },
    [code, submit]
  );

  // No session to challenge against (deep link, lost sessionStorage, etc.) — bounce back to sign-in.
  useEffect(() => {
    if (!mfaSession) {
      redirectTo(authorizeUrl());
    }
  }, [mfaSession]);

  // Refocus the (now-cleared) input on error so retyping just works, without stealing focus
  // away from the "Send new code" button etc. on states that don't show the input.
  useEffect(() => {
    if (state === 'incorrect' || state === 'expired') {
      inputRef.current?.focus();
    }
  }, [state]);

  if (!mfaSession) {
    return null;
  }

  const showInput = state !== 'lockedout';
  const inputErrorMessage =
    state === 'incorrect'
      ? "That code doesn't match. Check the code in your email and try again."
      : state === 'expired'
        ? 'This code has expired. Send a new code to continue.'
        : undefined;

  return (
    <div className="bg-primary flex h-full w-full items-center justify-center p-4">
      <div className="border-highlight bg-secondary flex w-full max-w-[400px] flex-col gap-[20px] rounded-2xl border p-[24px] text-[#ffffff]">
        <img src={logo} alt="Bioliberty Lifemind" className="mx-auto h-[60px] w-auto" />

        <div className="flex flex-col gap-[4px]">
          <Heading size="m">
            {state === 'incorrect' && 'Incorrect code'}
            {state === 'expired' && 'Your code has expired'}
            {state === 'lockedout' && 'Too many attempts'}
            {(state === 'entry' || state === 'generic-error') && 'Check your email'}
          </Heading>

          {(state === 'entry' || state === 'generic-error') && (
            <p className="text-sm text-white">
              We&apos;ve sent a 6-digit code to <strong>{mfaSession.maskedEmail}</strong>. Enter it below to sign in.
            </p>
          )}

          {state === 'lockedout' && (
            <p className="text-error text-sm">
              For security, we&apos;ve paused sign-in on this device. Try again in 30 minutes, or contact your facility
              administrator.
            </p>
          )}
        </div>

        {showInput && (
          <div className="flex w-full flex-col">
            <InputLabel htmlFor="mfa-code-input">One time passcode</InputLabel>
            <div className="border-input-border bg-secondary focus-within:border-accent flex items-center rounded-[8px] border p-[5px] text-sm focus-within:shadow-[inset_0_0_0_1px_var(--color-accent)]">
              <input
                ref={inputRef}
                id="mfa-code-input"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className={cn(
                  'w-full flex-1 bg-transparent leading-5 text-[#ffffff] placeholder:text-[#acb1b7] focus:outline-none',
                  isLoading && 'text-input-disabled-text cursor-not-allowed'
                )}
                value={code}
                placeholder="Enter your 6-digit code"
                onChange={handleCodeChange}
                onKeyDown={handleKeyDown}
                autoFocus={state === 'entry'}
                disabled={isLoading}
                data-testid="mfa-code-input"
              />
            </div>
            {inputErrorMessage && <ErrorMessage>{inputErrorMessage}</ErrorMessage>}
          </div>
        )}

        {state === 'generic-error' && <p className="text-error text-sm">Something went wrong. Please try again.</p>}

        {state === 'entry' && <p className="text-sm">Can&apos;t find the email? Check your spam folder.</p>}

        {state === 'expired' && (
          <Button
            variant="primary"
            onClick={restartSignIn}
            data-testid="mfa-send-new-code"
            className="bg-accent hover:bg-accent h-[32px] rounded-[8px] py-0 text-[#000716]"
          >
            Send new code
          </Button>
        )}

        {state === 'lockedout' && (
          <Button
            variant="primary"
            onClick={backToLogin}
            data-testid="mfa-back-to-login"
            className="bg-accent hover:bg-accent h-[32px] rounded-[8px] py-0 text-[#000716]"
          >
            Go back to login
          </Button>
        )}
      </div>
    </div>
  );
};
